import { useEffect, useState } from 'react';
import { Box, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, IconButton, Stack, TextField } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import CustomText from 'components/common/CustomText/CustomText';
import DiscussionNoteItem from 'components/case/DiscussionNoteItem/DiscussionNoteItem';
import { useAuth } from 'context/AuthContext';
import { useAddDiscussionNote, useGetDiscussionNotes } from 'hooks/useDiscussionNotes';
import { snackbarService } from 'services/snackbarService';
import { addRefetchListener } from 'services/appNotificationsService';
import { colors } from 'styles/colors';
import { EPermission } from 'enum/EPermission';
import useGlobalStyles from 'styles/globalStyles';

const PAGE_SIZE = 10;

interface Props {
  open: boolean;
  onClose: () => void;
  procedureId: number;
}

export default function DiscussionNotesModal({ open, onClose, procedureId }: Props) {
  const globalStyles = useGlobalStyles();
  const { user, doctor } = useAuth();
  const userId = doctor?.id ?? user.id;
  const isEditor = !doctor || doctor?.permission !== EPermission.VIEWER;

  const [newNote, setNewNote] = useState('');

  const [page, setPage] = useState(0);
  const [notes, setNotes] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  // Set atomically alongside notes inside onSuccess, instead of relying on the query's own
  // isLoading flag - that updates via react-query's internals on a different tick than our
  // local state, which can race with notes and briefly show the empty state.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // The modal is reused across cases (procedureId changes without unmounting) - reset local
  // pagination state so a different case's notes don't start from a stale accumulated list.
  useEffect(() => {
    setPage(0);
    setNotes([]);
    setTotalCount(0);
    setHasLoadedOnce(false);
  }, [procedureId]);

  const { isFetching, refetch } = useGetDiscussionNotes(userId, procedureId, page, PAGE_SIZE, {
    enabled: open,
    onSuccess: (data: any) => {
      const results = data?.results ?? [];
      setTotalCount(data?.count ?? 0);
      setNotes((prev) => (page === 0 ? results : [...prev, ...results]));
      setHasLoadedOnce(true);
    },
  });

  const hasMore = notes.length < totalCount;

  // A collaborator added/edited a note while this modal is open - live-refresh it.
  useEffect(() => {
    if (!open) return;
    return addRefetchListener('refetchDiscussionNotes', () => {
      if (page === 0) {
        refetch();
      } else {
        setPage(0);
      }
    });
  }, [open, refetch, page]);

  const { mutate: addMutation, isLoading: isAdding } = useAddDiscussionNote(
    () => {
      setNewNote('');
      if (page === 0) {
        refetch();
      } else {
        setPage(0);
      }
    },
    () => {
      snackbarService.showSnackbar('Failed to add note', 'error');
    },
  );

  const handleAdd = () => {
    const trimmed = newNote.trim();
    if (!trimmed) return;
    addMutation({ userId, procedureId, note: trimmed });
  };

  // Update/delete patch the local list in place instead of refetching, so accumulated
  // pages beyond the current one aren't discarded or duplicated by a single edit/delete.
  const handleNoteUpdated = (noteId: number, note: string) => {
    setNotes((prev) => prev.map((item) => (item.id === noteId ? { ...item, note } : item)));
  };

  const handleNoteDeleted = (noteId: number) => {
    setNotes((prev) => prev.filter((item) => item.id !== noteId));
    setTotalCount((prev) => Math.max(prev - 1, 0));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      className={globalStyles.modalContainer}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            boxShadow: '0px 12px 48px rgba(0,0,0,0.2)',
            overflow: 'hidden',
            height: 600,
            position: 'relative',
          },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        disableRipple
        sx={{
          position: 'absolute',
          top: 15,
          right: 15,
          backgroundColor: colors.primary,
          height: 25,
          width: 25,
        }}
      >
        <CloseIcon sx={{ fontSize: 15, color: colors.white }} />
      </IconButton>

      <DialogTitle className={globalStyles.modalTitle}>Notes</DialogTitle>
      <Divider />

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {!hasLoadedOnce && (
          <Box flex={1} display="flex" alignItems="center" justifyContent="center">
            <CircularProgress size={28} />
          </Box>
        )}

        {hasLoadedOnce && notes.length === 0 && (
          <Box flex={1} display="flex" alignItems="center" justifyContent="center">
            <CustomText text="No notes yet" color={colors.gray} />
          </Box>
        )}

        {hasLoadedOnce && notes.length > 0 && (
          <Stack spacing={1.5} sx={{ flex: 1, overflow: 'auto' }}>
            {notes.map((item: any) => (
              <DiscussionNoteItem
                key={item.id}
                item={item}
                userId={userId}
                procedureId={procedureId}
                isOwn={item.author?.id === user.id}
                onUpdated={(note) => handleNoteUpdated(item.id, note)}
                onDeleted={() => handleNoteDeleted(item.id)}
              />
            ))}

            {hasMore && (
              <Box display="flex" justifyContent="center" py={1}>
                {isFetching ? (
                  <CircularProgress size={20} />
                ) : (
                  <IconButton size="small" onClick={() => setPage((prev) => prev + 1)}>
                    <CustomText text="Load More" color={colors.link} fontSize={13} />
                  </IconButton>
                )}
              </Box>
            )}
          </Stack>
        )}

        {isEditor && (
          <Stack direction="row" spacing={1} alignItems="flex-end" pt={2}>
            <TextField
              fullWidth
              placeholder="Write a note for collaborators..."
              multiline
              maxRows={4}
              size="small"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
            />
            <IconButton
              onClick={handleAdd}
              disabled={isAdding || !newNote.trim()}
              sx={{
                backgroundColor: colors.primary,
                '&:hover': { backgroundColor: colors.primary },
                '&.Mui-disabled': { backgroundColor: colors.itemBorder },
              }}
            >
              {isAdding ? <CircularProgress size={18} sx={{ color: colors.white }} /> : <SendIcon sx={{ fontSize: 18, color: colors.white }} />}
            </IconButton>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
